import React, { useMemo, useState, useEffect, useContext } from 'react';
import 'rodal/lib/rodal.css';
import PropTypes from 'prop-types';
import { styled, css } from '@mui/system';
import { Modal as BaseModal } from '@mui/base/Modal';
import Fade from '@mui/material/Fade';
import { useTranslation } from 'react-i18next';
import { getLangWithKey } from '../../../lang';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import { userAuth } from '../../../contexts/userContext';
import customerOrGuestApiService from '../../../api/customerOrGuestApiService';
import { useNavigate } from 'react-router-dom';
import { CartConfModal } from './proCartModal';
import Label from '../../../components/bootstrap/forms/Label';
import { CartContext } from '../../../contexts/CartContext';
import { CardLabel, CardTitle } from '../../../components/bootstrap/Card';
import Input from '../../../components/bootstrap/forms/Input';
import './ProductMenuModal.css';

export const ProductMenuModal = ({
    loadalert,
    closemodel,
    data,
}) => {
    const { t, i18n } = useTranslation();
    const { token, setToken } = useContext(userAuth);
    const [qty, setQty] = useState(1);
    const [condialog, setCondialog] = useState(false);
    const [qtyError, setQtyError] = useState({ error: false, msg: t('*Required') });

    const navigate = useNavigate();
    const { addItemToCart } = useContext(CartContext);

    const availableStock = Number(data?.qty);
    const hasStock = !Number.isNaN(availableStock);
    const isIncreaseDisabled = hasStock ? qty >= availableStock : false;

    const totprice = useMemo(() => {
        const cleanPrice = Number((data?.price_nf || '0').replace(/,/g, ''));
        return qty * cleanPrice;
    }, [qty, data?.price_nf]);

    const handleDecrease = () => {
        if (qty > 1) setQty(qty - 1);
    };

    const handleIncrease = () => {
        if (hasStock && qty >= availableStock) return;
        setQty(qty + 1);
    };

    const handleChange = (e) => {
        const val = e.target.value;
        const numVal = Number(val);
        if (val === '') {
            setQty(1);
            setQtyError({ error: false, msg: t('*Required') });
            return;
        }
        if (!isNaN(numVal) && numVal >= 1) {
            setQty(numVal);
            setQtyError({ error: false, msg: t('*Required') });
        }
    };

    const addtempproduct = async () => {
        if (qty < 1 || !Number.isInteger(qty) || qty > data.qty) {
            setQtyError({ error: true, msg: t('Invalid Quantity') });
            return;
        }

        const formData = {
            branch_id: localStorage.getItem('selectedBranch'),
            prod_id: data.id,
            qty: qty,
            total_amount: totprice,
            action: 'add_individual_product'
        };

        try {
            const flechdata = await customerOrGuestApiService(formData, token, setToken, navigate);
            if (flechdata.type?.toLowerCase() === 'success') {
                addItemToCart();
                closemodel();
                setCondialog(true);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    return (
        <>
            <CartConfModal
                isopen={condialog}
                onclose={() => setCondialog(false)}
                continuebt={() => navigate('/products')}
                confirmpaybt={() => navigate('/cart')}
            />
            <Modal
                open={loadalert}
                onClose={closemodel}
                closeAfterTransition
                slots={{ backdrop: StyledBackdrop }}>
                <Fade in={loadalert}>
                    <ModalContent>
                        <div className='product-modal-container'>
                            <div className='top-container'>
                                <div className='header-section'>
                                    <CardLabel>
                                        <CardTitle className='header-text' tag='h4'>
                                            Product Purchase
                                        </CardTitle>
                                    </CardLabel>
                                </div>
                                <div className='divider' />
                                <div className='product-table'>
                                    <ul>
                                        <li className="product-row">
                                            <div className="product-name-col">
                                                <span>
                                                    {data?.name?.length > 30
                                                        ? data.name.substring(0, 30) + '...'
                                                        : data?.name}
                                                </span>
                                            </div>

                                            <div className="quantity-col">
                                                <Label>{t('Quantity')}</Label>
                                                <div className="qty-picker">
                                                    <button onClick={handleDecrease}>–</button>
                                                    <input
                                                        type='text'
                                                        value={qty}
                                                        onChange={handleChange}
                                                    />
                                                    <button 
                                                        onClick={handleIncrease} 
                                                        disabled={isIncreaseDisabled}
                                                    >+</button>
                                                </div>
                                                <DefultError error={qtyError} />
                                            </div>

                                            <div className="amount-col">
                                                <FormGroup id='totalAmount' label={t('Total Amount')} className='form-group'>
                                                    <Input
													// style={{ marginLeft: '75px' ,width: '120px'}}
                                                        type='number'
                                                        className='input-container'
                                                        disabled
                                                        value={totprice}
                                                    />
                                                </FormGroup>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className='bottom-container'>
                                <button className='cancel-button' onClick={closemodel}>
                                    {t('Close')}
                                </button>
                                <button className='confirm-button' onClick={addtempproduct}>
                                    {t('Confirm')}
                                </button>
                            </div>
                        </div>
                    </ModalContent>
                </Fade>
            </Modal>
        </>
    );
};

const DefultError = ({ error }) => (
    error.error && (
        <p className="error-msg">
            <span style={{ fontWeight: 'bold' }}>⚠</span>
            {error.msg}
        </p>
    )
);

// Styled Components for MUI Modal
const StyledBackdrop = styled('div')`
    z-index: -1;
    position: fixed;
    inset: 0;
    background-color: rgb(0 0 0 / 0.6);
`;

const Modal = styled(BaseModal)`
    position: fixed;
    z-index: 1300;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ModalContent = styled('div')(({ theme }) => css`
    width: 90%;
    max-width: 600px;
    background-color: #242632;
    border-radius: 20px;
    overflow: hidden;
    outline: none;
    @media (min-width: 900px) { width: 50%; }
`);
